import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Send, MessageCircle } from "lucide-react";
import { useApp } from "@/context/AppContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const AVATAR_URL =
  "https://customer-assets.emergentagent.com/job_homeatope-academy/artifacts/hc6iejs2_Skill%20avatarEnvaido%201.";

function genSessionId() {
  return (
    "ht_" +
    Math.random().toString(36).slice(2, 10) +
    "_" +
    Date.now().toString(36)
  );
}

export default function ChatPanel({ compact = false }) {
  const { t, lang } = useApp();
  const [sessionId] = useState(() => {
    const cached = typeof window !== "undefined" ? localStorage.getItem("ht_chat_session") : null;
    if (cached) return cached;
    const id = genSessionId();
    if (typeof window !== "undefined") localStorage.setItem("ht_chat_session", id);
    return id;
  });
  const [messages, setMessages] = useState([
    { role: "assistant", content: t.academy.chatFirstHi },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  // Update greeting on language change (only if conversation is still the initial greeting)
  useEffect(() => {
    setMessages((prev) => {
      if (prev.length === 1 && prev[0].role === "assistant") {
        return [{ role: "assistant", content: t.academy.chatFirstHi }];
      }
      return prev;
    });
  }, [lang, t.academy.chatFirstHi]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setSending(true);
    try {
      const res = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: text,
        language: lang,
      });
      const reply = res.data?.reply || t.academy.chatErrorFallback;
      setMessages((m) => [...m, { role: "assistant", content: reply }]);
    } catch (err) {
      setMessages((m) => [
        ...m,
        { role: "assistant", content: t.academy.chatErrorFallback },
      ]);
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      data-testid="chat-panel"
      className={`ht-glass flex flex-col overflow-hidden rounded-2xl ${
        compact ? "h-[520px]" : "h-[600px]"
      }`}
    >
      {/* Header */}
      <div
        className="flex items-center gap-3 border-b px-5 py-4"
        style={{ borderColor: "var(--ht-border)" }}
      >
        <div className="relative">
          <img
            src={AVATAR_URL}
            alt="Gerardo IA"
            className="h-11 w-11 rounded-full object-cover ht-pulse"
            style={{ backgroundColor: "var(--ht-surface)" }}
            data-testid="chat-avatar"
          />
          <span
            className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full"
            style={{ backgroundColor: "#22C55E", boxShadow: "0 0 0 2px var(--ht-surface)" }}
          />
        </div>
        <div className="flex flex-col leading-tight">
          <span className="text-sm font-semibold" style={{ color: "var(--ht-text)" }}>
            {t.academy.chatTitle}
          </span>
          <span className="text-[11px]" style={{ color: "var(--ht-text-muted)" }}>
            {t.academy.chatSubtitle}
          </span>
        </div>
      </div>

      {/* Messages */}
      <div
        ref={scrollRef}
        className="ht-scroll flex-1 space-y-3 overflow-y-auto px-4 py-4"
        data-testid="chat-messages"
      >
        {messages.map((m, idx) => (
          <div
            key={idx}
            className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
          >
            <div
              className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed ${
                m.role === "user" ? "ht-bubble-user" : "ht-bubble-bot"
              }`}
              data-testid={`chat-msg-${idx}`}
            >
              {m.content.split("\n").map((line, i) => (
                <p key={i} className={i ? "mt-1" : ""}>
                  {line}
                </p>
              ))}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start" data-testid="chat-thinking">
            <div className="ht-bubble-bot inline-flex items-center gap-2 px-3.5 py-2.5 text-sm" style={{ color: "var(--ht-text-muted)" }}>
              <span className="ht-pulse h-2 w-2 rounded-full" style={{ backgroundColor: "var(--ht-accent)" }} />
              {t.academy.chatThinking}
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <form
        onSubmit={send}
        className="flex items-center gap-2 border-t px-3 py-3"
        style={{ borderColor: "var(--ht-border)" }}
      >
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={t.academy.chatPlaceholder}
          data-testid="chat-input"
          className="flex-1 rounded-full border bg-transparent px-4 py-2.5 text-sm outline-none focus:ring-2"
          style={{
            borderColor: "var(--ht-border)",
            color: "var(--ht-text)",
          }}
        />
        <button
          type="submit"
          data-testid="chat-send-btn"
          disabled={sending || !input.trim()}
          className="ht-btn-accent flex h-10 w-10 items-center justify-center rounded-full disabled:opacity-50"
          aria-label="enviar"
        >
          <Send size={16} />
        </button>
      </form>
    </div>
  );
}

export function FloatingChatLauncher() {
  const { t } = useApp();
  return (
    <a
      href="#academia"
      data-testid="chat-launcher-floating"
      className="ht-btn-accent fixed bottom-24 right-5 z-30 hidden items-center gap-2 rounded-full px-4 py-3 text-sm font-semibold shadow-lg md:inline-flex"
    >
      <MessageCircle size={16} />
      {t.nav.askGerardo}
    </a>
  );
}
